import React, { useMemo } from 'react';

import { highlightText } from '@/shared/lib/text';
import { FilterDropdown, sortFilterOptions } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';
import { Icon } from '@/shared/ui/Icon';

import { useFilterStore } from '@/entities/discovery';
import { useAvailableTags } from '@/entities/tags';
import type { TagOption } from '@/entities/tags';

import styles from './TagDropdown.module.css';

const generateTagOptions = (availableTags: TagOption[], selectedTags: string[]): FilterDropdownOption[] => {
  if (availableTags.length === 0 && selectedTags.length === 0) return [];

  const options: FilterDropdownOption[] = availableTags.map((tag) => ({
    key: tag.value,
    label: tag.label,
    value: tag.value,
    disabled: false,
    data: {
      tagFQN: tag.value,
      classification: tag.classification,
      description: tag.description,
    },
  }));

  const missingTags = selectedTags
    .filter((tagFQN) => !options.some((opt) => opt.value === tagFQN))
    .map((tagFQN) => ({
      key: tagFQN,
      label: tagFQN.split('.').pop() || tagFQN,
      value: tagFQN,
      disabled: false,
      data: {
        tagFQN,
      },
    }));

  return sortFilterOptions([...options, ...missingTags], selectedTags);
};

type TagDropdownProps = {
  width?: number | string;
};

export const TagDropdown: React.FC<TagDropdownProps> = ({ width }) => {
  const { filters, updateTagsFilter, setTagsOperator } = useFilterStore();
  const { data: availableTags = [] } = useAvailableTags();

  const selectedTags = useMemo(() => filters.tags || [], [filters.tags]);
  const tagsOperator = useMemo(() => filters.tagsOperator || 'OR', [filters.tagsOperator]);

  const tagOptions = useMemo(() => {
    return generateTagOptions(availableTags, selectedTags);
  }, [availableTags, selectedTags]);

  return (
    <FilterDropdown
      buttonText="태그"
      searchPlaceholder="태그 검색..."
      selectedValues={selectedTags}
      options={tagOptions}
      onSelectionChange={updateTagsFilter}
      width={width}
      showOperatorToggle
      operator={tagsOperator}
      onOperatorChange={setTagsOperator}
      renderItem={(option, _isSelected, _onToggle, searchQuery) => {
        const classification = option.data?.classification as string | undefined;
        const displayText = classification ? `${option.label} (${classification})` : option.label;
        return (
          <div className={styles.tagContent}>
            <Icon name="tag" className={styles.tagIcon} />
            <span className={styles.tagLabel}>
              {highlightText(displayText, searchQuery, { className: 'highlight' })}
            </span>
          </div>
        );
      }}
    />
  );
};
